import axios, { AxiosError, AxiosResponse } from "axios";

import { configAxios } from "@/config/axios-config";
import { TokenStorage } from "@/data/infra/cache/async-storage";
import { handleNormalizeApiError } from "@/utils/normalize-api-error/normalize-api-error";

const REFRESH_PATH = "/auth/refresh";

// Lock global por módulo para evitar refresh concorrente na camada Web.
let isRefreshing = false;
let refreshPromise: Promise<string | null> | null = null;

/**
 * Função para refresh tokens de forma segura
 * @param bare - Axios instance sem interceptors para evitar loops no refresh
 * @returns - Access token ou null se não houver refresh token
 */
const refreshTokensSafe = async (bare: ReturnType<typeof axios.create>): Promise<string | null> => {
  // Se já estiver em processo de refresh, retorna a promise existente
  if (isRefreshing && refreshPromise) return refreshPromise;

  // Inicia o processo de refresh
  isRefreshing = true;

  // Cria uma promise para o refresh
  refreshPromise = (async () => {
    try {
      const refresh = await TokenStorage.getRefreshToken();
      if (!refresh) return null;

      const res = await bare.post(REFRESH_PATH, { refresh_token: refresh });
      const access_token = res.data?.access_token as string | undefined;
      const refresh_token = res.data?.refresh_token as string | undefined;

      if (!access_token) return null;

      await TokenStorage.setTokens(access_token, refresh_token ?? null);
      return access_token;
    } catch {
      return null;
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  })();

  // Retorna a promise do refresh
  return refreshPromise;
};

/**
 * Função para criar uma instância do Axios com interceptors
 * @param accessToken - Access token opcional para uso em casos específicos
 * @returns - Axios instance com interceptors para refresh tokens
 */
const createAxiosInstance = async (accessToken?: string) => {
  const instance = axios.create(configAxios);
  const bare = axios.create(configAxios); // sem interceptors para evitar loops no refresh

  instance.interceptors.request.use(
    async (config) => {
      const storedToken = await TokenStorage.getAccessToken();
      const tokenToUse = storedToken ?? accessToken;

      if (tokenToUse) {
        //@ts-expect-error
        config.headers = {
          ...config.headers,
          Authorization: `Bearer ${tokenToUse}`,
          Accept: "*/*",
        };
      }

      return config;
    },
    (error) => Promise.reject(error)
  );

  instance.interceptors.response.use(
    (response: AxiosResponse) => response,
    async (error: AxiosError<any>) => {
      let friendlyMessage = "Ocorreu um erro inesperado!";

      if (error.message === "Network Error") {
        friendlyMessage = "Sem conexão com a internet, tente novamente mais tarde!";
      } else if (error.code === "ECONNABORTED") {
        friendlyMessage = "Servidor demorou muito para responder, tente novamente mais tarde!";
      } else if (error.response) {
        const { status, data } = error.response;

        const original = (error.config || {}) as any;

        // 401 → refresh + retry (1x) para manter a sessão no browser.
        if (
          status === 401 &&
          !original._retry &&
          typeof original?.url === "string" &&
          !original.url.includes(REFRESH_PATH)
        ) {
          original._retry = true;

          const newToken = await refreshTokensSafe(bare);
          if (newToken) {
            original.headers = original.headers ?? {};
            original.headers["Authorization"] = `Bearer ${newToken}`;
            return instance.request(original);
          }

          await TokenStorage.clear();
          friendlyMessage = "Sessão expirada. Faça login novamente!";
        }

        if (status === 400 || status === 422) {
          friendlyMessage = handleNormalizeApiError(data);
        } else if (status === 401) {
          friendlyMessage = "Sessão expirada. Faça login novamente!";
        } else if (status === 403) {
          friendlyMessage = "Você não tem permissão para esta ação!";
        } else if (status === 404) {
          friendlyMessage = "Recurso não encontrado!";
        } else if (status === 500) {
          friendlyMessage = "Erro interno no servidor, tente novamente mais tarde!";
        } else {
          friendlyMessage = data?.message || `Erro inesperado (${status}).`;
        }
      }

      //  mantém AxiosError + response + status
      error.message = friendlyMessage;

      return Promise.reject(error);
    }
  );

  return instance;
};

export default { createAxiosInstance };
